import './SotGa4LabelingTable.scss';
import cn from 'classnames';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from '@Components/Dropdown';
import { DropdownVariant } from '@Components/Dropdown/dropdown-constants';
import { Checkbox } from '@Components/FormCheck';
import { FrozenColumnsTable } from '@Components/FrozenColumnsTable';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { CellRenderer, CornerLocation, Header } from '@Components/StickyTable';
import { Size } from '@Components/designConstants';
import { useContainerSize } from '../../utils/hooks/useContainerSize';
import { useMap } from '../../utils/hooks/useData';
import { CustomSegmentsData } from '../SegmentationMapping/segmentation-mapping-constants';
import {
  SotGa4EditsMap,
  SotGa4EditsMapAction,
  SotGa4EditsMapActionType,
  SotGa4MappingRow,
  SotGa4Row
} from './sot-ga4-mapping-tool-constants';

const LEFT_COLUMNS = [
  { name: 'raw_source', label: 'Raw Source' },
  { name: 'raw_medium', label: 'Raw Medium' },
  { name: 'campaign_name', label: 'Campaign Name' },
  { name: 'campaign_id', label: 'Campaign ID' },
  { name: 'ad_group_name', label: 'Ad Group Name' },
  { name: 'ad_group_id', label: 'Ad Group ID' },
  { name: 'ad_name', label: 'Ad Name' },
  { name: 'ad_id', label: 'Ad ID' },
  { name: 'analytics_account_id', label: 'Ad Analytics ID' }
];

const LEFT_DATA_FIELDS = ['campaign_key', ...LEFT_COLUMNS.map(column => column.name)];

const MIN_LEFT_WIDTH = 44;
const LEFT_SPACING = 24;

interface SotGa4LabelingTableProps {
  data: SotGa4MappingRow[];
  customSegments: CustomSegmentsData[] | undefined;
  editsMap: SotGa4EditsMap;
  selectedRows: Record<string, SotGa4Row>;
  dispatchEditsMap: Dispatch<SotGa4EditsMapAction>;
  setSelectedRows: Dispatch<SetStateAction<Record<string, SotGa4Row>>>;
}

export const SotGa4LabelingTable = ({
  data,
  customSegments,
  editsMap,
  selectedRows,
  dispatchEditsMap,
  setSelectedRows
}: SotGa4LabelingTableProps) => {
  const [selectAll, setSelectAll] = useState(false);
  const [focusedCell, setFocusedCell] = useState('');
  const [resizeActive, setResizeActive] = useState(false);
  const [resizedLeftWidth, setResizedLeftWidth] = useState<number | undefined>();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const containerSize = useContainerSize(containerRef, { widthOnly: true });

  const selectedRowsSet = useMemo(() => new Set(Object.keys(selectedRows)), [selectedRows]);

  // Keep the header checkbox honest when the selection is cleared externally
  // (tab switch, "Clear selection", save) rather than via this checkbox.
  useEffect(() => {
    if (selectAll && selectedRowsSet.size === 0) {
      setSelectAll(false);
    }
  }, [selectAll, selectedRowsSet]);

  const selectRow = useCallback(
    (row: SotGa4Row) => {
      const { campaign_key } = row;
      if (!campaign_key) {
        return;
      }

      if (Object.hasOwn(selectedRows, campaign_key)) {
        setSelectedRows(current => {
          const { [campaign_key]: removed, ...rest } = current;
          return rest;
        });
      } else {
        setSelectedRows(current => ({ ...current, [campaign_key]: row }));
      }
    },
    [selectedRows, setSelectedRows]
  );

  // Full original rows by key — the left renderer only receives a partial
  // object built from leftDataFields, but selectRow must store the full row.
  const dataMap = useMemo(() => {
    const output: Record<string, SotGa4MappingRow> = {};
    data.forEach(row => {
      if (row.campaign_key) {
        output[row.campaign_key] = row;
      }
    });
    return output;
  }, [data]);

  // Derived from customSegments (not rightTableHeaders) so its identity
  // survives focus/selection changes that rebuild the header renderers —
  // FrozenColumnsTable rebuilds its cell matrix whenever these names change.
  const rightHeaderNames = useMemo(
    () => (customSegments || []).map(segment => segment.segmentName),
    [customSegments]
  );

  const rightTableHeaders: Header[] = useMemo(
    () =>
      (customSegments || []).map(segment => ({
        label: segment.segmentName,
        name: segment.segmentName,
        width: 250,
        renderer: (row: SotGa4Row) => {
          if (!row?.campaign_key) {
            return <div />;
          }

          const rowToUse = editsMap[row.campaign_key] || row;
          const { campaign_key } = rowToUse;

          const val =
            (rowToUse.edits && Object.hasOwn(rowToUse.edits, segment.segmentName)
              ? rowToUse.edits[segment.segmentName]?.label
              : rowToUse[segment.segmentName.toLowerCase()]) || '';

          if (focusedCell === `${segment.segmentName}_${campaign_key}`) {
            return (
              <Dropdown
                size={Size.LG}
                clearable
                variant={DropdownVariant.CELL}
                className='select-container'
                value={val || null}
                placeholder={`Select ${segment.segmentName}`}
                onChange={selection => {
                  const selectedOption = segment.values.find(
                    value => String(value.valueId) === selection
                  );
                  // Issue an update when selecting an option from the dropdown, and
                  // issue a delete when clearing the value with the input's "x"
                  if (selectedOption) {
                    const rowsToApply = selectedRowsSet.has(campaign_key)
                      ? Object.values(selectedRows)
                      : [rowToUse];
                    dispatchEditsMap({
                      type: SotGa4EditsMapActionType.UPDATE_SEGMENT_EDIT,
                      updates: [
                        {
                          rows: rowsToApply,
                          edits: {
                            [segment.segmentName]: {
                              label: selectedOption.valueName,
                              value: String(selectedOption.valueId)
                            }
                          }
                        }
                      ]
                    });
                  } else {
                    const keys = selectedRowsSet.has(campaign_key)
                      ? Array.from(selectedRowsSet)
                      : [campaign_key];
                    dispatchEditsMap({
                      type: SotGa4EditsMapActionType.DELETE_SEGMENT_EDIT,
                      segmentName: segment.segmentName,
                      campaign_keys: keys
                    });
                  }
                }}
                options={segment.values.map(value => ({
                  label: value.valueName,
                  value: String(value.valueId)
                }))}
                reactSelectProps={{
                  autoFocus: true,
                  defaultMenuIsOpen: true,
                  onBlur: () => setFocusedCell('')
                }}
              />
            );
          }

          return (
            <InteractiveDiv
              className='editable-cell'
              data-testid={`sot-ga4-cell-${segment.segmentName}-${campaign_key}`}
              onClick={() => setFocusedCell(`${segment.segmentName}_${campaign_key}`)}
            >
              <span className='cell-text'>{val || '-'}</span>
            </InteractiveDiv>
          );
        }
      })),
    [customSegments, dispatchEditsMap, editsMap, focusedCell, selectedRows, selectedRowsSet]
  );

  const customLeftRenderer: CellRenderer<Record<string, unknown>> = useCallback(
    ({ data: rowData, style = {}, classes = [] }) => {
      const row = rowData as unknown as SotGa4MappingRow;

      return (
        <div style={style} className={cn(classes, 'left-cell')}>
          <div className='check-box-container'>
            <Checkbox
              className='checkbox'
              data-testid={`sot-ga4-row-check-${row.campaign_key}`}
              checked={row.campaign_key ? Object.hasOwn(selectedRows, row.campaign_key) : false}
              onChange={() => row.campaign_key && selectRow(dataMap[row.campaign_key])}
            />
          </div>
          {LEFT_COLUMNS.map(column => (
            <div
              key={column.name}
              className={cn('left-cell-div', column.name)}
              data-testid={`sot-ga4-left-${column.name}`}
            >
              <span className='cell-text'>{row[column.name] || '-'}</span>
            </div>
          ))}
        </div>
      );
    },
    [dataMap, selectRow, selectedRows]
  );

  // Populated by FrozenColumnsTable's internal effect, which builds the cell
  // matrix from `data` + header names and only rebuilds when those actually
  // change. Edited values reach cells through the header renderers (which read
  // editsMap at render time), so edits never require touching rightData —
  // that's what keeps single-cell edits O(visible) even at 500K rows.
  const [rightData, setRightData] = useState<{ originalRowIndex: number; content: unknown }[][]>(
    []
  );

  // Left-column fields are never editable, so sort keys come straight from
  // `data` via each row's originalRowIndex — a pointer reorder of the existing
  // matrix, with no cell reallocation. 'off' restores the original order.
  const sortRightData = useCallback(
    (columnName: string, direction: string) => {
      setRightData(prev => {
        const sorted = [...prev];
        sorted.sort((a, b) => {
          if (direction === 'off') {
            return a[0].originalRowIndex - b[0].originalRowIndex;
          }
          const valA = String(data[a[0].originalRowIndex]?.[columnName] ?? '');
          const valB = String(data[b[0].originalRowIndex]?.[columnName] ?? '');
          return direction === 'up' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        });
        return sorted;
      });
    },
    [data]
  );

  const [sortMap, setSortMapValue, setSortMap] = useMap<string, string>();

  // FrozenColumnsTable rebuilds rightData in original order when `data`
  // changes, so clear the sort carets to match.
  useEffect(() => {
    setSortMap({});
  }, [data, setSortMap]);

  const onSort = useCallback(
    (propertyName: string) => {
      const sortingTransitions: Record<string, string> = { off: 'up', up: 'down', down: 'off' };
      setSortMap({});
      const next = sortingTransitions[sortMap[propertyName] || 'off'];
      setSortMapValue(propertyName, next);
      sortRightData(propertyName, next);
    },
    [setSortMap, setSortMapValue, sortMap, sortRightData]
  );

  const customCornerRenderer = useCallback(
    (corner: CornerLocation) => {
      if (corner !== 'nw') {
        return <div />;
      }
      return (
        <div className='sticky-table-cell corner-header-cell'>
          <div className='check-box-container'>
            <Checkbox
              data-testid='sot-ga4-select-all'
              checked={selectAll}
              onChange={() => {
                if (selectAll) {
                  setSelectedRows({});
                } else {
                  const selectAllMap: Record<string, SotGa4Row> = {};
                  for (const row of data) {
                    if (row.campaign_key) {
                      selectAllMap[row.campaign_key] = row;
                    }
                  }

                  setSelectedRows(current => ({ ...current, ...selectAllMap }));
                }

                setSelectAll(!selectAll);
              }}
            />
          </div>
          {LEFT_COLUMNS.map(column => (
            <InteractiveDiv
              key={column.name}
              className={cn('include-sort-header-caret', column.name, sortMap[column.name])}
              data-testid={`sot-ga4-sort-${column.name}`}
              onClick={() => onSort(column.name)}
            >
              {column.label}
            </InteractiveDiv>
          ))}
        </div>
      );
    },
    [selectAll, sortMap, setSelectedRows, data, onSort]
  );

  const noRowsRenderer = useCallback(
    () =>
      data.length === 0 ? (
        <div className='no-rows' data-testid='sot-ga4-no-rows'>
          No rows to display.
        </div>
      ) : (
        <div />
      ),
    [data.length]
  );

  const startingWidth = (3 / 4) * (containerSize?.width ?? 0);

  const leftWidth = Math.max(MIN_LEFT_WIDTH, resizedLeftWidth ?? startingWidth);

  // Track the resize drag at the window level so it keeps following the
  // cursor outside the table and ends wherever the mouse is released.
  useEffect(() => {
    if (!resizeActive) {
      return undefined;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const containerLeft = containerRef.current?.getBoundingClientRect().left ?? 0;
      const halfAreaWidth = 10;
      const adjustedPosition = e.clientX - containerLeft - LEFT_SPACING - halfAreaWidth;
      setResizedLeftWidth(Math.max(MIN_LEFT_WIDTH, adjustedPosition));
    };
    const endResize = () => setResizeActive(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', endResize);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', endResize);
    };
  }, [resizeActive]);

  return (
    <div className='sot-ga4-labeling-table' data-testid='sot-ga4-labeling-table' ref={containerRef}>
      <div className='resize-container'>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          className='resize-area'
          data-testid='sot-ga4-resize-area'
          style={{ left: `${leftWidth + LEFT_SPACING}px` }}
          onMouseDown={() => setResizeActive(true)}
        />
      </div>
      <FrozenColumnsTable
        leftWidth={leftWidth}
        leftRenderer={customLeftRenderer}
        cornerRenderer={customCornerRenderer}
        rightTableHeaders={rightTableHeaders}
        rightTableHeaderNames={rightHeaderNames}
        originalRightData={data}
        leftDataFields={LEFT_DATA_FIELDS}
        rightData={rightData}
        setRightData={setRightData}
        noRowsRenderer={noRowsRenderer}
      />
    </div>
  );
};
